import { call, put, select } from 'redux-saga/effects'
import { reset } from 'redux-form'

import { Action, SmsSendResponse, Saga } from '../../types'
import { modalLoaderActions, signingActions, signingSelectors } from '../../redux'
import { getSigningForm } from './signing.mappers'
import { OpenApiInterface } from '../../api/requests/openApi'
import { Response } from '../../api/api'
import { ResponseError } from '../../utils/errors'
import { SmsVerifyResponse, SigningType } from '../../constants/enums'

export function* setSigningType(): Saga {
  const didSendFirstSms = yield select(signingSelectors.didSendFirstSms)

  if (!didSendFirstSms) {
    yield put(signingActions.sendFirstSms())
    yield put(signingActions.sendSms())
  }
}

export function* callSendSmsEndpoint(api: OpenApiInterface): Saga {
  const type: SigningType = yield select(signingSelectors.getSigningType)

  const endpoints = {
    [SigningType.Contract]: api.requestContractSmsCode,
    [SigningType.PreContract]: api.requestPreContractSmsCode,
    [SigningType.Scoring]: api.requestScoringSmsCode,
  }

  if (endpoints[type]) {
    return yield call(endpoints[type])
  }
  throw new ResponseError(`didnt find endpoint for ${type} - signing - call send sms endpoint`)
}

export function* sendSms(api: OpenApiInterface): Saga {
  const loader = yield put(modalLoaderActions.showModalLoader())
  const response = yield call(callSendSmsEndpoint, api)
  yield put(modalLoaderActions.hideModalLoader(loader?.id))

  if (response.responseStatus === Response.ok) {
    const { sent, nextAttemptAvailable } = response.data as SmsSendResponse
    yield put(signingActions.smsSent({ sent, nextAttemptAvailable }))
    yield put(reset('Signing'))
  } else {
    throw new ResponseError('signing - send sms saga', response)
  }
}

export function* callVerificationEndpoint(api: OpenApiInterface): Saga {
  const type: SigningType = yield select(signingSelectors.getSigningType)

  const payload = yield select(getSigningForm, type)

  const endpoints = {
    [SigningType.Contract]: api.verifyContractSmsCode,
    [SigningType.PreContract]: api.verifyPreContractSmsCode,
    [SigningType.Scoring]: api.verifyScoringSmsCode,
  }

  if (endpoints[type]) {
    return yield call(endpoints[type], payload)
  }
  throw new ResponseError(`didnt find endpoint for ${type} - signing - call verification endpoint`)
}

export function* submitVerification(
  api: OpenApiInterface,
  { payload: { success, error } }: Action<any>
): Saga {
  const loader = yield put(modalLoaderActions.showModalLoader())
  const response = yield call(callVerificationEndpoint, api)
  yield put(modalLoaderActions.hideModalLoader(loader?.id))

  if (response.responseStatus === Response.ok) {
    const verificationResponse = response.data?.status
    yield put(signingActions.setVerificationCode(verificationResponse))

    if (verificationResponse === SmsVerifyResponse.Ok) {
      yield put(signingActions.clearSigningState())
      yield call(success)
    } else {
      yield call(error)
    }
  } else {
    throw new ResponseError('signing - send verification saga', response)
  }
}
